// @owners { team: patients-team }
import { SPACING } from '@alto/design-library-tokens';
import { MdPadding, SmSpacing } from '@alto/design-system';
import {
  InsuranceBinInput,
  InsuranceGroupInput,
  InsuranceMemberIdInput,
  InsuranceNicknameInput,
  InsurancePcnInput,
  InsuranceRelationshipInputSelect,
} from '@alto/features';
import React from 'react';
import type { Control, FieldErrors } from 'react-hook-form';
import { useController } from 'react-hook-form';
import { RELATIONSHIP_CODES } from '~shared/constants';
import { type Values } from '~shared/features/insurances/helpers/getInitialInsuranceFormValues';
import { createValidator, validatePresentIf, validatePresentWithMessage } from '~shared/validations';

type FormikHandleChange = {
  (e: React.ChangeEvent<any>): void;
  <T = string | React.ChangeEvent<any>>(
    field: T,
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  ): T extends React.ChangeEvent<any> ? void : (e: string | React.ChangeEvent<any>) => void;
};

type Props =
  | {
      readonly source: 'rhf';
      readonly control: Control<Values>;
      readonly isSubmitted: boolean;
      readonly values?: never;
      readonly submitCount?: never;
      readonly errors?: never;
      readonly handleChange?: never;
    }
  | {
      readonly source: 'formik';
      readonly values: Values;
      readonly submitCount: number;
      readonly errors: Record<string, string>;
      readonly handleChange: FormikHandleChange;
      readonly control?: never;
      readonly isSubmitted?: never;
    };

export const validate = createValidator<Values>({
  relationship_code: validatePresentWithMessage('Relationship'),
  first_name: validatePresentIf((_, values) => {
    return values.relationship_code !== RELATIONSHIP_CODES.SELF;
  }, 'First Name'),
  last_name: validatePresentIf((_, values) => {
    return values.relationship_code !== RELATIONSHIP_CODES.SELF;
  }, 'Last Name'),
  member_id: validatePresentWithMessage('Member ID'),
  bin: validatePresentWithMessage('BIN'),
});

function InsuranceFormFieldsRHF({ control, isSubmitted }: { control: Control<Values>; isSubmitted: boolean }) {
  const memberId = useController({ control, name: 'member_id' });
  const bin = useController({ control, name: 'bin' });
  const group = useController({ control, name: 'group' });
  const pcn = useController({ control, name: 'pcn' });
  const relationshipCode = useController({ control, name: 'relationship_code' });
  const firstName = useController({ control, name: 'first_name' });
  const lastName = useController({ control, name: 'last_name' });
  const nickname = useController({ control, name: 'nickname' });

  const getError = (err: FieldErrors<Values>[keyof Values]) => (isSubmitted ? err?.message : undefined);

  return (
    <>
      <SmSpacing />
      <MdPadding
        leftPadding={SPACING.STATIC.LG}
        rightPadding={SPACING.STATIC.LG}
      >
        <InsuranceMemberIdInput
          error={getError(memberId.fieldState.error)}
          onChange={memberId.field.onChange}
          value={memberId.field.value ?? ''}
        />
      </MdPadding>
      <MdPadding
        leftPadding={SPACING.STATIC.LG}
        rightPadding={SPACING.STATIC.LG}
      >
        <InsuranceBinInput
          error={getError(bin.fieldState.error)}
          onChange={bin.field.onChange}
          value={bin.field.value ?? ''}
        />
      </MdPadding>
      <MdPadding
        leftPadding={SPACING.STATIC.LG}
        rightPadding={SPACING.STATIC.LG}
      >
        <InsuranceGroupInput
          error={getError(group.fieldState.error)}
          value={group.field.value ?? ''}
          onChange={group.field.onChange}
        />
      </MdPadding>
      <MdPadding
        leftPadding={SPACING.STATIC.LG}
        rightPadding={SPACING.STATIC.LG}
      >
        <InsurancePcnInput
          error={getError(pcn.fieldState.error)}
          onChange={pcn.field.onChange}
          value={pcn.field.value ?? ''}
        />
      </MdPadding>
      <MdPadding
        leftPadding={SPACING.STATIC.LG}
        rightPadding={SPACING.STATIC.LG}
      >
        <InsuranceRelationshipInputSelect
          firstName={firstName.field.value}
          firstNameError={getError(firstName.fieldState.error)}
          lastName={lastName.field.value}
          lastNameError={getError(lastName.fieldState.error)}
          onChangeFirstName={firstName.field.onChange}
          onChangeLastName={lastName.field.onChange}
          onChangeRelationship={relationshipCode.field.onChange}
          relationshipCode={relationshipCode.field.value ?? ''}
          relationshipCodeError={getError(relationshipCode.fieldState.error)}
        />
      </MdPadding>
      <MdPadding
        leftPadding={SPACING.STATIC.LG}
        rightPadding={SPACING.STATIC.LG}
      >
        <InsuranceNicknameInput
          error={getError(nickname.fieldState.error)}
          value={nickname.field.value ?? ''}
          onChange={nickname.field.onChange}
        />
      </MdPadding>
      <SmSpacing />
    </>
  );
}

function InsuranceFormFieldsFormik({
  values,
  submitCount,
  errors,
  handleChange,
}: {
  values: Values;
  submitCount: number;
  errors: Record<string, string>;
  handleChange: FormikHandleChange;
}) {
  const hasSubmitted = submitCount > 0;
  const getErrorForField = (fieldName: keyof Values) => (hasSubmitted ? errors[fieldName] : undefined);
  return (
    <>
      <SmSpacing />
      <MdPadding
        leftPadding={SPACING.STATIC.LG}
        rightPadding={SPACING.STATIC.LG}
      >
        <InsuranceMemberIdInput
          error={getErrorForField('member_id')}
          onChange={handleChange('member_id')}
          value={values.member_id ?? ''}
        />
      </MdPadding>
      <MdPadding
        leftPadding={SPACING.STATIC.LG}
        rightPadding={SPACING.STATIC.LG}
      >
        <InsuranceBinInput
          error={getErrorForField('bin')}
          onChange={handleChange('bin')}
          value={values.bin ?? ''}
        />
      </MdPadding>
      <MdPadding
        leftPadding={SPACING.STATIC.LG}
        rightPadding={SPACING.STATIC.LG}
      >
        <InsuranceGroupInput
          error={getErrorForField('group')}
          value={values.group ?? ''}
          onChange={handleChange('group')}
        />
      </MdPadding>
      <MdPadding
        leftPadding={SPACING.STATIC.LG}
        rightPadding={SPACING.STATIC.LG}
      >
        <InsurancePcnInput
          error={getErrorForField('pcn')}
          onChange={handleChange('pcn')}
          value={values.pcn ?? ''}
        />
      </MdPadding>
      <MdPadding
        leftPadding={SPACING.STATIC.LG}
        rightPadding={SPACING.STATIC.LG}
      >
        <InsuranceRelationshipInputSelect
          firstName={values.first_name}
          firstNameError={getErrorForField('first_name')}
          lastName={values.last_name}
          lastNameError={getErrorForField('last_name')}
          onChangeFirstName={handleChange('first_name')}
          onChangeLastName={handleChange('last_name')}
          onChangeRelationship={handleChange('relationship_code')}
          relationshipCode={values.relationship_code ?? ''}
          relationshipCodeError={getErrorForField('relationship_code')}
        />
      </MdPadding>
      <MdPadding
        leftPadding={SPACING.STATIC.LG}
        rightPadding={SPACING.STATIC.LG}
      >
        <InsuranceNicknameInput
          error={getErrorForField('nickname')}
          value={values.nickname ?? ''}
          onChange={handleChange('nickname')}
        />
      </MdPadding>
      <SmSpacing />
    </>
  );
}

export const InsuranceFormFields = (props: Props) => {
  if (props.source === 'rhf') {
    return (
      <InsuranceFormFieldsRHF
        control={props.control}
        isSubmitted={props.isSubmitted}
      />
    );
  }
  return (
    <InsuranceFormFieldsFormik
      errors={props.errors}
      handleChange={props.handleChange}
      submitCount={props.submitCount}
      values={props.values}
    />
  );
};
